import { P2GTextField } from '@parcel2go/core-ui';
import { MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { QuoteRequirement } from '~/lib/types/basket-types';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import {
  ChangeEvent,
  ComponentProps,
  HTMLInputAutoCompleteAttribute,
  HTMLInputTypeAttribute,
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { InputModeType, InputModes } from './input-mode-type';
import { MapRequirementOptions } from '~/lib/utils/map-requirement-options';

export type RequirementFieldProps = {
  id: string;
  translationKey: string;
  loading: boolean;
  update: (value: string) => void;
  data: string | number | undefined;
  errors: Record<string, string> | undefined;
  dataTestId?: string;
  defaultLabel?: string;
  label?: string;
  startAdornment?: string;
  endAdornment?: string;
  defaultMinValue?: number;
  defaultMaxValue?: number;
  defaultMinLength?: number;
  defaultMaxLength?: number;
  defaultStep?: number;
  defaultRequired?: boolean;
  forcedRequired?: boolean;
  defaultType?: string;
  forceType?: string;
  options?: { value: string; label: string }[];
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onEnter?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  forcedError?: string;
  unitStart?: boolean;
  unitEnd?: boolean;
  autoFocus?: boolean;
  tabIndex?: number;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  inputProps?: ComponentProps<typeof P2GTextField>['inputProps'];
  autoComplete?: HTMLInputAutoCompleteAttribute;
  unmasked?: boolean;
};

type Props = {
  requirement: QuoteRequirement | undefined | null;
} & RequirementFieldProps;

function RequirementField({
  requirement,
  loading,
  update,
  data,
  id,
  errors,
  dataTestId,
  translationKey,
  defaultLabel,
  startAdornment,
  endAdornment,
  defaultMaxValue,
  defaultMinValue,
  defaultStep,
  defaultRequired,
  defaultType,
  defaultMaxLength,
  defaultMinLength,
  options,
  onBlur,
  forcedError,
  unitStart,
  unitEnd,
  onEnter,
  label,
  autoFocus,
  tabIndex,
  inputRef,
  onFocus,
  autoComplete,
  forcedRequired,
  disabled,
  inputProps,
  forceType,
  unmasked
}: Props) {
  const { track } = useAnalytics();
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const [entered, setEntered] = useState<Date>();
  const isSelect = requirement?.valueType === 'select' || (options && options.length > 0);
  const [inlineValid, setInlineValid] = useState(false);
  const ref = createRef<HTMLInputElement | HTMLTextAreaElement>();
  const usableRef = inputRef || ref;

  useEffect(() => {
    if (isSelect && !data && options && options?.length > 0 && !options.find((o) => o.value === data)) {
      update(options[0].value);
    }
  }, [isSelect, data, options, update]);

  const type = useMemo(() => {
    if (forceType) {
      return forceType;
    }
    return requirement?.valueType || defaultType;
  }, [defaultType, forceType, requirement]);

  const inputType: HTMLInputTypeAttribute = useMemo(() => {
    if (id.indexOf('email') > -1) {
      return 'email';
    }
    if (id.indexOf('phone') > -1) {
      return 'tel';
    }
    return type === 'integer' || type === 'decimal' ? 'number' : 'text';
  }, [id, type]);

  const inputMode = useMemo<InputModeType>(() => {
    if (type === 'decimal') {
      return 'decimal';
    }
    if (inputType === 'tel') {
      return 'tel';
    }
    if (inputType === 'email') {
      return 'email';
    }
    return inputType === 'number' ? 'numeric' : InputModes.find((x) => x === type);
  }, [inputType, type]);

  const isInErrorState = useMemo(() => {
    if (inlineValid) {
      return forcedError !== undefined;
    }
    return forcedError !== undefined || (errors && !!errors[id]);
  }, [errors, forcedError, id, inlineValid]);

  useEffect(() => setInlineValid(false), [errors]);

  useEffect(() => {
    if (errors && !!errors[id]) {
      if (usableRef.current) {
        try {
          setInlineValid(!usableRef.current.checkValidity || usableRef.current.checkValidity());
        } catch (e) {
          console.error(e);
        }
      }
    }
  }, [data, errors, id, usableRef]);

  const onFieldFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setEntered(new Date());
      if (onFocus) onFocus(e);
    },
    [onFocus]
  );

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      update(e.target.value);
      try {
        setInlineValid(!e.target.checkValidity || e.target.checkValidity());
      } catch (e) {
        console.error(e);
      }
    },
    [update]
  );

  const onBlurWrapper = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(e);
      }
      if (entered && id) {
        const time = new Date().getTime() - entered.getTime();
        track({ type: 'checkout_field_focus', payload: { field: id, time }, persist: false });
      }
    },
    [entered, id, onBlur, track]
  );

  useEffect(() => {
    if (usableRef.current && typeof usableRef.current.setCustomValidity === 'function') {
      usableRef.current.setCustomValidity(forcedError || '');
    }
  }, [usableRef, forcedError]);

  const fieldProps = useMemo(() => {
    const props = inputProps || {};
    if (unmasked) {
      props['data-sentry-unmask'] = true;
    }
    return props;
  }, [unmasked, inputProps]);

  const visibleOptions = useMemo(() => {
    if (isSelect) {
      if (options?.length) {
        return options;
      }

      if (requirement?.metadata?.options?.length) {
        return MapRequirementOptions(requirement.metadata.options);
      }
    }
    return [];
  }, [isSelect, options, requirement?.metadata?.options]);

  const required = forcedRequired || (requirement?.required ?? (defaultRequired || false));

  if (isSelect) {
    return (
      <P2GTextField
        dataTestId={dataTestId || id}
        id={id}
        label={t(translationKey.toUpperCase(), {
          defaultValue: requirement?.name ?? defaultLabel
        })}
        value={data || ''}
        onChange={onChange}
        disabled={loading || disabled}
        required={required}
        fullWidth
        select
        error={isInErrorState}
        errorText={forcedError || (errors && errors[id])}
        onBlur={onBlurWrapper}
        onFocus={onFieldFocus}
        autoFocus={autoFocus}
        tabIndex={tabIndex}
        inputRef={usableRef}
        inputProps={fieldProps}
      >
        {visibleOptions?.map((item) => (
          <MenuItem key={`${item.label}-${item.value}`} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </P2GTextField>
    );
  }

  return (
    <P2GTextField
      dataTestId={dataTestId || id}
      id={id}
      type={inputType}
      fullWidth
      label={
        t(translationKey.toUpperCase(), {
          defaultValue: label ?? requirement?.name ?? defaultLabel
        }) + (required ? '' : ` (${t('COMMON_INPUT_OPTIONAL', { defaultValue: 'Optional' })})`)
      }
      placeholder={requirement?.metadata?.placeholder}
      minLength={requirement?.metadata?.minLength || defaultMinLength}
      maxLength={requirement?.metadata?.maxLength || defaultMaxLength}
      min={requirement?.metadata?.minValue || defaultMinValue}
      max={requirement?.metadata?.maxValue || defaultMaxValue}
      helperText={requirement?.metadata?.helperText}
      pattern={
        inputType === 'email' ? `[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}$` : requirement?.metadata?.regex
      }
      value={data || ''}
      onInput={onChange}
      required={required}
      disabled={loading || disabled}
      error={isInErrorState}
      errorText={
        forcedError ||
        (errors && errors[id]
          ? requirement?.metadata?.example
            ? `${errors[id]}\nExample: ${requirement?.metadata.example}`
            : errors[id]
          : undefined)
      }
      startAdornment={startAdornment || (unitStart && requirement?.metadata?.unit)}
      endAdornment={endAdornment || (unitEnd && requirement?.metadata?.unit)}
      step={type === 'decimal' ? 0.01 : type === 'integer' ? 1 : defaultStep}
      onFocus={onFieldFocus}
      onBlur={onBlurWrapper}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && onEnter) {
          onEnter(e);
        }
      }}
      // This can be removed when this card is done IBUG-1655
      InputLabelProps={{
        shrink: undefined
      }}
      autoFocus={autoFocus}
      tabIndex={tabIndex}
      inputRef={usableRef}
      inputProps={{
        autoComplete: autoComplete === 'off' ? 'turn-off' : autoComplete,
        'aria-autocomplete': autoComplete === 'off' ? 'none' : undefined,
        inputMode: inputMode,
        ...fieldProps
      }}
    />
  );
}

export default RequirementField;
